package com.mgacreative.hotel.feature.hotel.ui.auth

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.outlined.AccessTime
import androidx.compose.material.icons.outlined.BarChart
import androidx.compose.material.icons.outlined.DesktopWindows
import androidx.compose.material.icons.outlined.Email
import androidx.compose.material.icons.outlined.Lock
import androidx.compose.material.icons.outlined.PhoneAndroid
import androidx.compose.material.icons.outlined.Shield
import androidx.compose.material.icons.outlined.Visibility
import androidx.compose.material.icons.outlined.VisibilityOff
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.feature.hotel.ui.components.AppLogo
import com.mgacreative.hotel.feature.hotel.ui.components.downloadDesktopExe
import com.mgacreative.hotel.feature.hotel.ui.components.hotelHeroPainter
import com.mgacreative.hotel.feature.hotel.ui.components.openAndroidPlayStore
import com.mgacreative.hotel.shared.ui.designsystem.components.HFLoadingState
import com.mgacreative.hotel.shared.ui.designsystem.components.LanguageSelectorRow
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n

private class ArchHeroShape : Shape {
    override fun createOutline(
        size: Size,
        layoutDirection: LayoutDirection,
        density: Density
    ): Outline {
        val path = Path().apply {
            moveTo(0f, 0f)
            lineTo(size.width * 0.82f, 0f)
            cubicTo(
                size.width * 1.04f, size.height * 0.32f,
                size.width * 1.04f, size.height * 0.68f,
                size.width * 0.82f, size.height
            )
            lineTo(0f, size.height)
            close()
        }
        return Outline.Generic(path)
    }
}

@Composable
fun LoginScreen(viewModel: AuthViewModel) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var memberCode by remember { mutableStateOf("") }
    var passwordVisible by remember { mutableStateOf(false) }
    var rememberMe by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        val savedEmail = viewModel.getRememberedEmail()
        val savedCode = viewModel.getRememberedMemberCode()
        if (!savedEmail.isNullOrBlank()) {
            email = savedEmail
            rememberMe = true
        }
        if (!savedCode.isNullOrBlank()) {
            memberCode = savedCode
        }
    }

    val isLoading by viewModel.isLoading.collectAsState()
    val error by viewModel.errorMessage.collectAsState()

    if (isLoading) {
        HFLoadingState()
        return
    }

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF090F1E))
    ) {
        val isWideScreen = maxWidth >= 860.dp

        if (isWideScreen) {
            val archShape = remember { ArchHeroShape() }

            // Wide Screen: 2 Column Hero with Curved Golden Light Edge + White Login Card
            Row(
                modifier = Modifier.fillMaxSize(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Left Column: Hero Lobby Image with Arch + Golden Light Glow Line
                Box(
                    modifier = Modifier
                        .weight(1.22f)
                        .fillMaxHeight()
                ) {
                    // Curved Hero Image
                    Image(
                        painter = hotelHeroPainter(),
                        contentDescription = "Hotel Lobby",
                        contentScale = ContentScale.Crop,
                        modifier = Modifier
                            .fillMaxSize()
                            .clip(archShape)
                    )

                    // Subtle Dark Gradient Overlay for bottom text readability
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .clip(archShape)
                            .background(
                                Brush.verticalGradient(
                                    0.0f to Color(0x10000000),
                                    0.5f to Color(0x30000000),
                                    0.75f to Color(0x80040814),
                                    1.0f to Color(0xF0060C1B)
                                )
                            )
                    )

                    // Glowing Golden / Amber Light Border Line along the convex curve
                    Spacer(
                        modifier = Modifier
                            .fillMaxSize()
                            .drawWithContent {
                                val arcPath = Path().apply {
                                    moveTo(size.width * 0.82f, 0f)
                                    cubicTo(
                                        size.width * 1.04f, size.height * 0.32f,
                                        size.width * 1.04f, size.height * 0.68f,
                                        size.width * 0.82f, size.height
                                    )
                                }
                                // Soft outer amber glow
                                drawPath(
                                    path = arcPath,
                                    brush = Brush.verticalGradient(
                                        listOf(
                                            Color(0x33F59E0B),
                                            Color(0x66F59E0B),
                                            Color(0x88D97706),
                                            Color(0x33B45309)
                                        )
                                    ),
                                    style = Stroke(width = 7f, cap = StrokeCap.Round)
                                )
                                // Crisp warm golden light stroke
                                drawPath(
                                    path = arcPath,
                                    brush = Brush.verticalGradient(
                                        listOf(
                                            Color(0xFFFEF08A),
                                            Color(0xFFFBBF24),
                                            Color(0xFFF59E0B),
                                            Color(0xFFD97706),
                                            Color(0xFFB45309)
                                        )
                                    ),
                                    style = Stroke(width = 2.2f, cap = StrokeCap.Round)
                                )
                            }
                    )

                    // Bottom Borderless Feature Items (Strictly inside image)
                    Row(
                        modifier = Modifier
                            .align(Alignment.BottomStart)
                            .padding(bottom = 36.dp, start = 28.dp, end = 100.dp)
                            .fillMaxWidth(0.72f),
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        FeatureBadge(
                            icon = Icons.Outlined.Shield,
                            title = l10n("Güvenli"),
                            modifier = Modifier.weight(1f)
                        )
                        FeatureBadge(
                            icon = Icons.Outlined.AccessTime,
                            title = l10n("7/24 Erişim"),
                            modifier = Modifier.weight(1f)
                        )
                        FeatureBadge(
                            icon = Icons.Outlined.BarChart,
                            title = l10n("Raporlama"),
                            modifier = Modifier.weight(1f)
                        )
                    }
                }

                // Right Column: Pure White Login Form Card
                Box(
                    modifier = Modifier
                        .weight(0.95f)
                        .fillMaxHeight(),
                    contentAlignment = Alignment.Center
                ) {
                    LoginFormCard(
                        email = email,
                        onEmailChange = { email = it },
                        password = password,
                        onPasswordChange = { password = it },
                        memberCode = memberCode,
                        onMemberCodeChange = { memberCode = it.uppercase() },
                        passwordVisible = passwordVisible,
                        onPasswordVisibilityToggle = { passwordVisible = !passwordVisible },
                        rememberMe = rememberMe,
                        onRememberMeChange = { rememberMe = it },
                        error = error,
                        onLoginClick = { 
                            viewModel.clearError()
                            viewModel.login(email, password, memberCode, rememberMe) 
                        },
                        onForgotPasswordClick = { viewModel.authNavigator.navigateTo(AuthScreenType.FORGOT_PASSWORD) },
                        onRegisterClick = { viewModel.authNavigator.navigateTo(AuthScreenType.REGISTER) },
                        modifier = Modifier
                            .widthIn(max = 440.dp)
                            .fillMaxWidth()
                            .padding(24.dp)
                    )
                }
            }
        } else {
            // Mobile / Smartphone Screen: Native Adaptive Layout with Top Hero Header & White Card
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .imePadding()
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Mobile Top Header: Hero Image with Bottom Curve & Gold Light Line
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(180.dp)
                ) {
                    Image(
                        painter = hotelHeroPainter(),
                        contentDescription = "Hotel Header",
                        contentScale = ContentScale.Crop,
                        modifier = Modifier.fillMaxSize()
                    )
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(
                                Brush.verticalGradient(
                                    colors = listOf(
                                        Color(0x33000000),
                                        Color(0x66000000),
                                        Color(0xEE090F1E)
                                    )
                                )
                            )
                    )

                    // 3 Badges in Mobile Header
                    Row(
                        modifier = Modifier
                            .align(Alignment.BottomCenter)
                            .padding(bottom = 12.dp, start = 16.dp, end = 16.dp)
                            .fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceEvenly
                    ) {
                        FeatureBadge(
                            icon = Icons.Outlined.Shield,
                            title = l10n("Güvenli"),
                            iconSize = 24.dp,
                            fontSize = 11.sp
                        )
                        FeatureBadge(
                            icon = Icons.Outlined.AccessTime,
                            title = l10n("7/24 Erişim"),
                            iconSize = 24.dp,
                            fontSize = 11.sp
                        )
                        FeatureBadge(
                            icon = Icons.Outlined.BarChart,
                            title = l10n("Raporlama"),
                            iconSize = 24.dp,
                            fontSize = 11.sp
                        )
                    }
                }

                // Mobile Main Login Card
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    contentAlignment = Alignment.Center
                ) {
                    LoginFormCard(
                        email = email,
                        onEmailChange = { email = it },
                        password = password,
                        onPasswordChange = { password = it },
                        memberCode = memberCode,
                        onMemberCodeChange = { memberCode = it.uppercase() },
                        passwordVisible = passwordVisible,
                        onPasswordVisibilityToggle = { passwordVisible = !passwordVisible },
                        rememberMe = rememberMe,
                        onRememberMeChange = { rememberMe = it },
                        error = error,
                        onLoginClick = { 
                            viewModel.clearError()
                            viewModel.login(email, password, memberCode, rememberMe) 
                        },
                        onForgotPasswordClick = { viewModel.authNavigator.navigateTo(AuthScreenType.FORGOT_PASSWORD) },
                        onRegisterClick = { viewModel.authNavigator.navigateTo(AuthScreenType.REGISTER) },
                        isCompact = true,
                        modifier = Modifier
                            .widthIn(max = 440.dp)
                            .fillMaxWidth()
                    )
                }
                
                Spacer(Modifier.height(16.dp))
            }
        }
    }
}

@Composable
private fun FeatureBadge(
    icon: ImageVector,
    title: String,
    modifier: Modifier = Modifier,
    iconSize: androidx.compose.ui.unit.Dp = 32.dp,
    fontSize: androidx.compose.ui.unit.TextUnit = 13.sp
) {
    Column(
        modifier = modifier.padding(vertical = 4.dp, horizontal = 2.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            tint = Color(0xFFFBBF24), // Vibrant luxury gold / amber accent
            modifier = Modifier.size(iconSize)
        )
        Spacer(Modifier.height(6.dp))
        Text(
            text = title,
            fontSize = fontSize,
            fontWeight = FontWeight.Bold,
            color = Color.White,
            textAlign = TextAlign.Center
        )
    }
}

@Composable
private fun LoginFormCard(
    email: String,
    onEmailChange: (String) -> Unit,
    password: String,
    onPasswordChange: (String) -> Unit,
    memberCode: String,
    onMemberCodeChange: (String) -> Unit,
    passwordVisible: Boolean,
    onPasswordVisibilityToggle: () -> Unit,
    rememberMe: Boolean,
    onRememberMeChange: (Boolean) -> Unit,
    error: String?,
    onLoginClick: () -> Unit,
    onForgotPasswordClick: () -> Unit,
    onRegisterClick: () -> Unit,
    isCompact: Boolean = false,
    modifier: Modifier = Modifier
) {
    Card(
        shape = RoundedCornerShape(28.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 16.dp),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = modifier
    ) {
        Column(
            modifier = Modifier
                .padding(
                    horizontal = if (isCompact) 20.dp else 32.dp,
                    vertical = if (isCompact) 20.dp else 28.dp
                )
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Language selector on top of login card
            LanguageSelectorRow(
                modifier = Modifier.padding(bottom = 12.dp)
            )

            // App Logo
            AppLogo(
                modifier = Modifier
                    .size(if (isCompact) 72.dp else 84.dp)
                    .padding(bottom = 4.dp)
            )

            // Title & Subtitle
            Text(
                text = l10n("Hoş Geldiniz"),
                fontSize = 24.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF0F172A)
            )
            Spacer(Modifier.height(6.dp))
            Text(
                text = l10n("Otel Yönetim Sistemine Giriş Yapın"),
                fontSize = 12.sp,
                color = Color(0xFF64748B),
                textAlign = TextAlign.Center,
                lineHeight = 16.sp
            )

            Spacer(Modifier.height(20.dp))

            // Email Input
            OutlinedTextField(
                value = email,
                onValueChange = onEmailChange,
                placeholder = { Text(l10n("E-posta adresiniz"), color = Color(0xFF94A3B8), fontSize = 14.sp) },
                leadingIcon = {
                    Icon(
                        imageVector = Icons.Outlined.Email,
                        contentDescription = null,
                        tint = Color(0xFF64748B),
                        modifier = Modifier.size(20.dp)
                    )
                },
                singleLine = true,
                shape = RoundedCornerShape(12.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = Color(0xFF0F172A),
                    unfocusedBorderColor = Color(0xFFE2E8F0),
                    focusedContainerColor = Color.White,
                    unfocusedContainerColor = Color.White
                ),
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(Modifier.height(14.dp))

            // Password Input
            OutlinedTextField(
                value = password,
                onValueChange = onPasswordChange,
                placeholder = { Text(l10n("Şifreniz"), color = Color(0xFF94A3B8), fontSize = 14.sp) },
                leadingIcon = {
                    Icon(
                        imageVector = Icons.Outlined.Lock,
                        contentDescription = null,
                        tint = Color(0xFF64748B),
                        modifier = Modifier.size(20.dp)
                    )
                },
                trailingIcon = {
                    IconButton(onClick = onPasswordVisibilityToggle) {
                        Icon(
                            imageVector = if (passwordVisible) Icons.Outlined.Visibility else Icons.Outlined.VisibilityOff,
                            contentDescription = null,
                            tint = Color(0xFF94A3B8),
                            modifier = Modifier.size(20.dp)
                        )
                    }
                },
                visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                singleLine = true,
                shape = RoundedCornerShape(12.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = Color(0xFF0F172A),
                    unfocusedBorderColor = Color(0xFFE2E8F0),
                    focusedContainerColor = Color.White,
                    unfocusedContainerColor = Color.White
                ),
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(Modifier.height(14.dp))

            // Member Code Input (Admin Tarafından Verilen Benzersiz Üye Kodu)
            OutlinedTextField(
                value = memberCode,
                onValueChange = onMemberCodeChange,
                placeholder = { Text(l10n("Üye Kodu (Örn: MBR-A8X92)"), color = Color(0xFF94A3B8), fontSize = 14.sp) },
                leadingIcon = {
                    Icon(
                        imageVector = Icons.Outlined.Shield,
                        contentDescription = null,
                        tint = Color(0xFFD97706),
                        modifier = Modifier.size(20.dp)
                    )
                },
                singleLine = true,
                shape = RoundedCornerShape(12.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = Color(0xFFD97706),
                    unfocusedBorderColor = Color(0xFFE2E8F0),
                    focusedContainerColor = Color.White,
                    unfocusedContainerColor = Color.White
                ),
                modifier = Modifier.fillMaxWidth(),
                supportingText = {
                    Text(
                        text = l10n("Admin tarafından tanımlanan benzersiz üyelik kodu"),
                        fontSize = 11.sp,
                        color = Color(0xFF64748B)
                    )
                }
            )

            Spacer(Modifier.height(10.dp))

            // Remember Me & Forgot Password Row
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.clickable { onRememberMeChange(!rememberMe) }
                ) {
                    Checkbox(
                        checked = rememberMe,
                        onCheckedChange = onRememberMeChange,
                        colors = CheckboxDefaults.colors(
                            checkedColor = Color(0xFF0F172A),
                            uncheckedColor = Color(0xFF94A3B8)
                        ),
                        modifier = Modifier.size(32.dp)
                    )
                    Text(
                        text = l10n("Beni hatırla"),
                        fontSize = 12.sp,
                        color = Color(0xFF334155),
                        fontWeight = FontWeight.Medium
                    )
                }

                Text(
                    text = l10n("Şifremi Unuttum?"),
                    fontSize = 12.sp,
                    color = Color(0xFF64748B),
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.clickable { onForgotPasswordClick() }
                )
            }

            val err = error
            if (err != null) {
                Spacer(Modifier.height(8.dp))
                Surface(
                    color = Color(0xFFFEF2F2),
                    shape = RoundedCornerShape(8.dp),
                    border = BorderStroke(1.dp, Color(0xFFFCA5A5)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = err,
                        color = Color(0xFFDC2626),
                        fontSize = 12.sp,
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp)
                    )
                }
            }

            Spacer(Modifier.height(20.dp))

            // Primary Login Button
            Button(
                onClick = onLoginClick,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(48.dp),
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color(0xFF0B192C)
                )
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    Text(
                        text = l10n("Giriş Yap"),
                        fontSize = 15.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Color.White
                    )
                    Spacer(Modifier.width(8.dp))
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.ArrowForward,
                        contentDescription = null,
                        tint = Color.White,
                        modifier = Modifier.size(18.dp)
                    )
                }
            }

            Spacer(Modifier.height(24.dp))

            // Register Link
            Row(
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = l10n("Hesabınız yok mu?"),
                    fontSize = 12.sp,
                    color = Color(0xFF64748B)
                )
                Spacer(Modifier.width(4.dp))
                Text(
                    text = l10n("Kayıt Ol"),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF0F172A),
                    modifier = Modifier.clickable { onRegisterClick() }
                )
            }

            Spacer(Modifier.height(20.dp))

            // Download Section Divider & Header
            HorizontalDivider(
                color = Color(0xFFE2E8F0),
                thickness = 1.dp,
                modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp)
            )

            Spacer(Modifier.height(14.dp))

            Text(
                text = l10n("Uygulamayı İndirin").uppercase(),
                fontSize = 10.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF94A3B8),
                letterSpacing = 1.sp
            )

            Spacer(Modifier.height(12.dp))

            // Download Buttons (Desktop .exe & Android Google Play)
            DownloadButtonsRow()
        }
    }
}

@Composable
private fun DownloadButtonsRow(modifier: Modifier = Modifier) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Desktop .EXE Button
        AppDownloadBadge(
            icon = Icons.Outlined.DesktopWindows,
            platformName = "Windows .EXE",
            subTitle = l10n("Masaüstü"),
            accentColor = Color(0xFF38BDF8),
            modifier = Modifier.weight(1f),
            onClick = {
                downloadDesktopExe()
            }
        )

        // Android (Google Play) Button
        AppDownloadBadge(
            icon = Icons.Outlined.PhoneAndroid,
            platformName = "Google Play",
            subTitle = l10n("Android"),
            accentColor = Color(0xFF34D399),
            modifier = Modifier.weight(1f),
            onClick = {
                openAndroidPlayStore()
            }
        )
    }
}

@Composable
private fun AppDownloadBadge(
    icon: ImageVector,
    platformName: String,
    subTitle: String,
    accentColor: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(14.dp),
        color = Color(0xFF0F172A),
        border = BorderStroke(1.dp, Color(0xFF334155)),
        shadowElevation = 2.dp,
        modifier = modifier.height(48.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 8.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Start
        ) {
            Box(
                modifier = Modifier
                    .size(32.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(Color(0xFF1E293B)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = platformName,
                    tint = accentColor,
                    modifier = Modifier.size(18.dp)
                )
            }

            Spacer(Modifier.width(8.dp))

            Column(
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.Start
            ) {
                Text(
                    text = subTitle,
                    fontSize = 9.sp,
                    fontWeight = FontWeight.Medium,
                    color = Color(0xFF94A3B8),
                    lineHeight = 11.sp
                )
                Text(
                    text = platformName,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                    lineHeight = 13.sp
                )
            }
        }
    }
}
